<template>
  <aside
    class="sidebar"
    :class="{ 'mobile-open': isMobileMenuOpen }"
    :aria-label="$t('sidebar.workspace')"
  >
    <nav class="sidebar-nav">
      <div class="sidebar-section">
        <div class="section-label">{{ $t('sidebar.workspace') }}</div>
        <button
          v-for="item in workspaceItems"
          :key="item.view"
          type="button"
          class="menu-item"
          :class="{ active: currentView === item.view }"
          :aria-current="currentView === item.view ? 'page' : undefined"
          @click="handleViewChange(item.view)"
        >
          <span v-if="item.view === 'Profile'" class="material-icons-outlined profile-nav-icon" aria-hidden="true">person_outline</span>
          <span v-else class="menu-icon" :class="item.icon"></span>
          <span class="menu-label">{{ $t(`sidebar.${item.view}`) }}</span>
        </button>
      </div>
      <div class="sidebar-section">
        <div class="section-label">{{ $t('sidebar.create') }}</div>
        <button type="button" class="menu-item" :class="{ active: $route.meta.creationTool === 'youtube' }" @click="showYouTubeImporter">
          <span class="menu-icon icon-youtube"></span>
          <span class="menu-label">{{ $t('sidebar.YouTubeImport') }}</span>
        </button>
        <button type="button" class="menu-item" :class="{ active: $route.meta.creationTool === 'transcribe' }" @click="showUploader">
          <span class="menu-icon icon-transcribe"></span>
          <span class="menu-label">{{ $t('sidebar.Transcribe') }}</span>
        </button>
        <button type="button" class="menu-item" :class="{ active: $route.meta.creationTool === 'speech' }" @click="showTextToSpeech">
          <span class="menu-icon icon-speech"></span>
          <span class="menu-label">{{ $t('sidebar.TextToSpeech') }}</span>
        </button>
      </div>
      <div class="sidebar-section sidebar-section-bottom">
        <button
          type="button"
          class="menu-item referral-item"
          :class="{ active: currentView === 'Referral' }"
          :aria-current="currentView === 'Referral' ? 'page' : undefined"
          @click="handleViewChange('Referral')"
        >
          <span class="menu-icon icon-gift"></span>
          <span class="menu-label">{{ $t('sidebar.Referral') }}</span>
        </button>
      </div>
    </nav>
    <div class="sidebar-footer">
      <MembershipStatus
        :loading="!userDataReady"
        :is-premium="isPremium"
        :is-pro="isPro"
        :remaining-usage="remainingUsage"
        :monthly-remaining-usage="monthlyRemainingUsage"
        :max-monthly-usage="maxMonthlyUsage"
        :purchased-credits="purchasedCredits"
        :purchased-credits-total="purchasedCreditsTotal"
        @upgrade="handleUpgrade"
      />
    </div>
  </aside>
</template>

<script>
import { computed } from 'vue'
import MembershipStatus from '../common/MembershipStatus.vue'
import { useAuth } from '@/composables/auth'
import { useViews } from '@/composables/views'
import { useModals } from '@/composables/modals'
import { useMobileMenu } from '@/composables/useMobileMenu'

export default {
  name: 'Sidebar',
  components: {
    MembershipStatus
  },
  setup() {
    const { userDetails, userDataReady } = useAuth()
    const { currentView, handleViewChange: _handleViewChange } = useViews()
    const {
      showTranscribeInterface,
      showYouTubeImporter: _showYouTubeImporter,
      showTextToSpeech: _showTextToSpeech,
      handleUpgrade
    } = useModals()
    const { isMobileMenuOpen, closeMobileMenu } = useMobileMenu()

    const isPremium = computed(() => userDetails.value?.isPremium || false)
    const isPro = computed(() => userDetails.value?.isPro || false)
    const remainingUsage = computed(() => userDetails.value?.remainingUsage || 0)
    const monthlyRemainingUsage = computed(() => userDetails.value?.monthlyRemainingUsage || 0)
    const maxMonthlyUsage = computed(() => userDetails.value?.maxMonthlyUsage || 200)
    const purchasedCredits = computed(() => userDetails.value?.purchasedCredits || 0)
    const purchasedCreditsTotal = computed(() => userDetails.value?.purchasedCreditsTotal || 0)
    const workspaceItems = [
      { view: 'Material', icon: 'icon-material' },
      { view: 'RecentFiles', icon: 'icon-recent' },
      { view: 'LearningRecords', icon: 'icon-learning' },
      { view: 'Profile' },
      { view: 'VocabularyBook', icon: 'icon-vocabulary' },
      { view: 'CheckIn', icon: 'icon-calendar' }
    ]

    const handleViewChange = (view) => {
      _handleViewChange(view)
      if (window.innerWidth <= 768) {
        closeMobileMenu()
      }
    }

    const showUploader = () => {
      showTranscribeInterface()
      if (window.innerWidth <= 768) {
        closeMobileMenu()
      }
    }

    const showTextToSpeech = () => {
      _showTextToSpeech()
      if (window.innerWidth <= 768) {
        closeMobileMenu()
      }
    }

    const showYouTubeImporter = () => {
      _showYouTubeImporter()
      if (window.innerWidth <= 768) {
        closeMobileMenu()
      }
    }

    return {
      workspaceItems,
      currentView,
      userDataReady,
      isPremium,
      isPro,
      remainingUsage,
      monthlyRemainingUsage,
      maxMonthlyUsage,
      purchasedCredits,
      purchasedCreditsTotal,
      isMobileMenuOpen,
      handleViewChange,
      showUploader,
      showYouTubeImporter,
      showTextToSpeech,
      handleUpgrade
    }
  }
}
</script>

<style scoped>
.profile-nav-icon { width: 20px; flex-shrink: 0; font-size: 20px; }

.sidebar {
  position: fixed;
  top: var(--app-navbar-height);
  bottom: 0;
  left: 0;
  z-index: 10;
  width: var(--sidebar-width);
  display: flex;
  flex-direction: column;
  border-right: 1px solid var(--border-subtle);
  background: var(--color-page);
}

.sidebar-nav {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: var(--space-4) var(--space-3);
}

.sidebar-section + .sidebar-section {
  margin-top: var(--space-5);
}

.sidebar-section + .sidebar-section-bottom {
  margin-top: auto;
  padding-top: var(--space-5);
}

.section-label {
  padding: 0 var(--space-3) var(--space-2);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  font-weight: 600;
}

.menu-item {
  width: 100%;
  min-height: 36px;
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  text-align: left;
  cursor: pointer;
  transition: background-color 160ms ease, color 160ms ease;
}

.menu-item + .menu-item {
  margin-top: 2px;
}

.menu-item:hover {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.menu-item.active {
  background: var(--surface-active);
  color: var(--ink-primary);
  font-weight: 600;
}

.menu-item.active .menu-icon {
  color: var(--signal-active-strong);
}

.menu-item:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.menu-icon {
  flex: 0 0 18px;
  width: 18px;
  height: 18px;
  color: var(--ink-tertiary);
  background: currentColor;
  mask-position: center;
  mask-repeat: no-repeat;
  mask-size: contain;
  -webkit-mask-position: center;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-size: contain;
}

.menu-item:hover .menu-icon {
  color: var(--ink-secondary);
}

.referral-item {
  color: var(--ink-primary);
  font-weight: 500;
}

.referral-item .menu-icon,
.referral-item:hover .menu-icon,
.referral-item.active .menu-icon {
  color: var(--signal-reward);
}

.menu-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.icon-calendar { mask-image: url('~@/assets/ui-icons/calendar.svg'); -webkit-mask-image: url('~@/assets/ui-icons/calendar.svg'); }
.icon-recent { mask-image: url('~@/assets/ui-icons/history.svg'); -webkit-mask-image: url('~@/assets/ui-icons/history.svg'); }
.icon-material { mask-image: url('~@/assets/ui-icons/library.svg'); -webkit-mask-image: url('~@/assets/ui-icons/library.svg'); }
.icon-learning { mask-image: url('~@/assets/ui-icons/sparkles.svg'); -webkit-mask-image: url('~@/assets/ui-icons/sparkles.svg'); }
.icon-vocabulary { mask-image: url('~@/assets/ui-icons/text.svg'); -webkit-mask-image: url('~@/assets/ui-icons/text.svg'); }
.icon-transcribe { mask-image: url('~@/assets/ui-icons/captions.svg'); -webkit-mask-image: url('~@/assets/ui-icons/captions.svg'); }
.icon-youtube { mask-image: url('~@/assets/ui-icons/play.svg'); -webkit-mask-image: url('~@/assets/ui-icons/play.svg'); }
.icon-gift { mask-image: url('~@/assets/ui-icons/gift.svg'); -webkit-mask-image: url('~@/assets/ui-icons/gift.svg'); }
.icon-speech { mask-image: url('~@/assets/ui-icons/voice.svg'); -webkit-mask-image: url('~@/assets/ui-icons/voice.svg'); }

.sidebar-footer {
  flex: 0 0 auto;
  padding: var(--space-4) var(--space-5) var(--space-5);
  border-top: 1px solid var(--border-subtle);
}

@media (max-width: 768px) {
  .sidebar {
    top: 3.5rem;
    z-index: 1000;
    width: min(280px, calc(100vw - 3rem));
    background: var(--surface-panel);
    box-shadow: var(--shadow-md);
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
  }

  .sidebar.mobile-open {
    transform: translateX(0);
  }
}
</style>
